import {
  JobPlacement,
  PlanEditGesture,
  PlanEditIntent,
  RegistrationResource,
  RouteResources
} from '@core/models/plan-edit-gesture.model';

export const PLAN_EDIT_MAX_INTENTS = 50;

function toWirePlacement(p: JobPlacement): Record<string, string> {
  return p.kind === 'EndOf'
    ? { kind: p.kind, route_uid: p.routeUid }
    : { kind: p.kind, route_uid: p.routeUid, action_uid: p.actionUid };
}

function setTime(routeUid: string, actionUid: string, at: string | null): PlanEditIntent {
  return {
    intentType: 'UserSetActionTimeIntent',
    payload: { route_uid: routeUid, action_uid: actionUid, at }
  };
}

function moveJob(jobUid: string, placement: JobPlacement): PlanEditIntent {
  return {
    intentType: 'UserMoveJobIntent',
    payload: { job_uid: jobUid, placement: toWirePlacement(placement) }
  };
}

function moveActions(actionUids: string[], placement: JobPlacement): PlanEditIntent {
  return {
    intentType: 'UserMoveActionsIntent',
    payload: { action_uids: actionUids, placement: toWirePlacement(placement) }
  };
}

// DL rejects unknown keys, so an absent driver or trailer is left out rather than sent as null.
function setResources(r: RouteResources): PlanEditIntent {
  const payload: Record<string, unknown> = {
    route_uid: r.routeUid,
    vehicle_uid: r.vehicleUid
  };
  if (r.driverUid !== null) payload['driver_uid'] = r.driverUid;
  if (r.trailerUid !== null) payload['trailer_uid'] = r.trailerUid;
  return { intentType: 'UserSetRouteResourcesIntent', payload };
}

function setCompany(routeUid: string, companyUid: string | null): PlanEditIntent {
  return {
    intentType: 'UserSetRouteCompanyIntent',
    payload: { route_uid: routeUid, company_uid: companyUid }
  };
}

const REGISTRATION_KEYS: Record<RegistrationResource, string> = {
  VEHICLE: 'vehicle',
  TRAILER: 'trailer',
  DRIVER: 'driver'
};

/** One intent per resource: DL takes exactly one key in `registration`, null releasing it. */
function setRegistrations(
  routeUid: string,
  registrations: Partial<Record<RegistrationResource, string | null>> = {}
): PlanEditIntent[] {
  return (Object.keys(REGISTRATION_KEYS) as RegistrationResource[])
    .filter((resource) => registrations[resource] !== undefined)
    .map((resource) => {
      const code = registrations[resource];
      return {
        intentType: 'UserSetRouteRegistrationIntent',
        payload: {
          route_uid: routeUid,
          registration: {
            [REGISTRATION_KEYS[resource]]:
              code === null || code === undefined ? null : { code }
          }
        }
      };
    });
}

function openRoute(
  gesture: Extract<PlanEditGesture, { kind: 'open-route' }>
): PlanEditIntent {
  const payload: Record<string, unknown> = {
    temporary_route_uid: gesture.routeRef,
    vehicle_uid: gesture.vehicleUid
  };
  if (gesture.driverUid !== null) payload['driver_uid'] = gesture.driverUid;
  if (gesture.trailerUid !== null) payload['trailer_uid'] = gesture.trailerUid;
  if (gesture.companyUid !== null) payload['company_uid'] = gesture.companyUid;
  return { intentType: 'UserOpenRouteIntent', payload };
}

export function intentsFor(gesture: PlanEditGesture): PlanEditIntent[] {
  const clears = 'clearsCompanyOf' in gesture ? (gesture.clearsCompanyOf ?? []) : [];
  return [...clears.map((routeUid) => setCompany(routeUid, null)), ...ownIntents(gesture)];
}

function ownIntents(gesture: PlanEditGesture): PlanEditIntent[] {
  switch (gesture.kind) {
    case 'pin':
      return [setTime(gesture.routeUid, gesture.actionUid, gesture.at)];
    case 'release-pin':
      return [setTime(gesture.routeUid, gesture.actionUid, null)];
    case 'reorder':
      return [moveActions([gesture.actionUid], gesture.placement)];
    case 'move-stop':
      return gesture.placement.routeUid === gesture.fromRouteUid
        ? [moveActions([gesture.actionUid], gesture.placement)]
        : [
            ...gesture.jobUids.map((jobUid) => moveJob(jobUid, gesture.placement)),
            ...(gesture.releasesPin
              ? [setTime(gesture.fromRouteUid, gesture.actionUid, null)]
              : [])
          ];
    case 'move-job':
      return [
        moveJob(gesture.jobUid, gesture.placement),
        ...(gesture.releasesPin
          ? [setTime(gesture.fromRouteUid, gesture.fromActionUid, null)]
          : [])
      ];
    case 'move-tour':
      return [moveActions(gesture.actionUids, gesture.placement)];
    case 'set-resources':
      return [
        ...(gesture.companyUid === undefined
          ? []
          : [setCompany(gesture.resources.routeUid, gesture.companyUid)]),
        setResources(gesture.resources),
        ...setRegistrations(gesture.resources.routeUid, gesture.registrations)
      ];
    case 'set-company':
      return [setCompany(gesture.routeUid, gesture.companyUid)];
    case 'swap-resources':
      return [setResources(gesture.a), setResources(gesture.b)];
    case 'set-route-start':
      return [
        {
          intentType: 'UserSetRouteStartIntent',
          payload: { route_uid: gesture.routeUid, at: gesture.at }
        }
      ];
    case 'open-route':
      return [openRoute(gesture), ...(gesture.then ? intentsFor(gesture.then) : [])];
    case 'remove-route':
      return [
        { intentType: 'UserRemoveRouteIntent', payload: { route_uid: gesture.routeUid } }
      ];
    case 'set-lock':
      return [
        {
          intentType: 'UserSetRouteLockIntent',
          payload: { route_uid: gesture.routeUid, lock_cfg: gesture.lockCfg }
        }
      ];
  }
}

/** The routes a gesture changes, which show as pending until DL answers it. */
export function touchedRouteUids(gesture: PlanEditGesture): string[] {
  switch (gesture.kind) {
    case 'pin':
    case 'release-pin':
    case 'reorder':
    case 'set-lock':
    case 'set-route-start':
    case 'set-company':
      return [gesture.routeUid];
    case 'move-stop':
    case 'move-job':
    case 'move-tour':
      return [...new Set([gesture.fromRouteUid, gesture.placement.routeUid])];
    case 'set-resources':
      return [gesture.resources.routeUid];
    case 'swap-resources':
      return [gesture.a.routeUid, gesture.b.routeUid];
    case 'open-route':
      return [
        ...new Set([
          gesture.routeRef,
          ...(gesture.then ? touchedRouteUids(gesture.then) : [])
        ])
      ];
    case 'remove-route':
      return [gesture.routeUid];
  }
}
